import { BlackPopupRolagem } from "@/components/blog/BlackPopupRolagem";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowRight, ChevronLeft, ChevronRight, MonitorPlay, GraduationCap, BadgeCheck, BookOpen, UserCheck, Infinity as InfinityIcon, Star } from "lucide-react";
import { SiteHeader } from "@/components/feo/SiteHeader";
import { BlackDestaqueSection } from "@/components/black2x1/BlackDestaqueSection";
import { SiteFooter } from "@/components/feo/SiteFooter";
import { PROFESSORES_BY_PRODUCT } from "@/components/posfigma/professores-by-product";
import { DEPOIMENTOS } from "@/lib/depoimentos/data";

const DOCENTE_PREVIEW: { foto: string; nome: string }[] = (() => {
  const seen = new Set<string>();
  const out: { foto: string; nome: string }[] = [];
  for (const product of ["orto", "dor", "esporte", "geronto", "trauma", "prescricao"] as const) {
    for (const p of PROFESSORES_BY_PRODUCT[product]) {
      if (seen.has(p.nome)) continue;
      seen.add(p.nome);
      out.push({ foto: p.foto, nome: p.nome });
      if (out.length >= 9) return out;
    }
  }
  return out;
})();

import {
  HERO_IMG,
  HERO_SRCSET,
  HERO_SIZES,
  selectSlidesFromConfig,
  type Slide,
} from "@/components/home/banner-slides";
import { useBlackfeo } from "@/hooks/use-blackfeo";
import { BannerSlideView } from "@/components/home/BannerSlideView";

import { getAcoesStateFn } from "@/lib/campanhas/acoes.functions";
import { getBannerConfigFn } from "@/lib/home/banner.functions";
import cardPos from "@/assets/feo/card-pos.webp";
import cardCert from "@/assets/feo/card-cert.webp";
import cardStar from "@/assets/feo/card-star.webp";
import helio from "@/assets/feo/helio.webp";
import leandro from "@/assets/feo/leandro.webp";
import lucas from "@/assets/feo/lucas.webp";
import rafael from "@/assets/feo/rafael.webp";


export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Fisio em Ortopedia | Pós-graduações em Fisioterapia" },
      { name: "description", content: "Pós-graduações EAD reconhecidas pelo MEC, Certificações de 60h, Star Cursos e AprovaFEO em fisioterapia musculoesquelética." },
      { property: "og:title", content: "Fisio em Ortopedia | Pós-graduações e Educação em Fisioterapia" },
      { property: "og:description", content: "Pós-graduações EAD reconhecidas pelo MEC, Certificações de 60h, Star Cursos e AprovaFEO em fisioterapia musculoesquelética." },
      { property: "og:url", content: "https://fisioemortopedia.com.br/" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Fisio em Ortopedia | Pós-graduações e Educação em Fisioterapia" },
      { name: "twitter:description", content: "Pós-graduações EAD reconhecidas pelo MEC, Certificações de 60h, Star Cursos e AprovaFEO. Educação em fisioterapia musculoesquelética desde 2019." },

    ],
    links: [
      { rel: "canonical", href: "https://fisioemortopedia.com.br/" },
      {
        rel: "preload",
        as: "image",
        href: HERO_IMG,
        imageSrcSet: HERO_SRCSET,
        imageSizes: HERO_SIZES,
        fetchPriority: "high",
      },
    ],
  }),
  loader: async () => {
    const [acoes, banner] = await Promise.all([getAcoesStateFn(), getBannerConfigFn()]);
    return { acoes, banner };
  },
  component: HomePage,
});


const FEATURES = [
  { icon: MonitorPlay, title: "Aulas 100% Online", desc: "Inovação em ensino à distância com a mesma certificação do presencial." },
  { icon: GraduationCap, title: "Professores Referência", desc: "Aulas com profissionais que são referência nas suas áreas no Brasil." },
  { icon: BadgeCheck, title: "Reconhecida pelo MEC", desc: "Programas de Pós-Graduação Lato Sensu reconhecidos pelo Ministério da Educação." },
  { icon: BookOpen, title: "Evidências Científicas", desc: "Conteúdos pautados nas principais evidências e práticas difundidas mundialmente." },
  { icon: UserCheck, title: "Ensino Individual", desc: "Controle o seu tempo e ritmo de estudo para assistir às aulas e realizar avaliações." },
  { icon: InfinityIcon, title: "Programa de 360h", desc: "Aulas gravadas e encontros ao vivo com os professores do programa." },
];

const PRODUCTS = [
  {
    img: cardPos,
    tag: "Pós-graduações",
    imgAlt: "Cursos de pós-graduação em fisioterapia",
    desc: "Programas Lato Sensu exclusivos para fisioterapeutas. Todos com 360h, 100% online e reconhecidos pelo MEC.",
    cta: "Conheça os cursos",
    href: "/pos",
    internal: true,
  },
  {
    img: cardCert,
    tag: "Certificações",
    imgAlt: "Certificações profissionais para fisioterapeutas",
    desc: "Cursos 100% online com o que há de mais novo na ciência. Certificações repletas de aplicações clínicas e aulas práticas.",
    cta: "Conheça as certificações",
    href: "/certificacao",
    internal: true,
  },
  {
    img: cardStar,
    tag: "Star Cursos",
    imgAlt: "Star Cursos, assinatura anual com mais de 100 cursos",
    desc: "Aprimore suas decisões clínicas com base em evidências. Faça a assinatura anual e acesse +100 cursos imediatamente.",
    cta: "Conheça a Star",
    href: "/star",
    internal: true,
  },
];


const STATS = [
  { value: "+7", label: "Anos no mercado" },
  { value: "8", label: "Congressos realizados" },
  { value: "+120", label: "Cursos na Star Cursos" },
  { value: "40K", label: "Alunos em todo o Brasil" },
];

const FOUNDERS = [
  { name: "Hélio Nichioka", role: "Professor e Fisioterapeuta", photo: helio },
  { name: "Leandro Fukusawa", role: "Professor e Fisioterapeuta", photo: leandro },
  { name: "Lucas Nery", role: "Professor e Fisioterapeuta", photo: lucas },
  { name: "Rafael Alaiti", role: "Professor e Fisioterapeuta", photo: rafael },
];

function StarRating({ n }: { n: number }) {
  return (
    <div className="flex items-center gap-0.5">
      <span className="sr-only">{n} de 5 estrelas</span>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          aria-hidden="true"
          className={
            i < n
              ? "h-4 w-4 fill-amber-400 text-amber-400"
              : "h-4 w-4 fill-slate-200 text-slate-200"
          }
        />
      ))}
    </div>
  );
}



function RotatingBanner({ slides }: { slides: Slide[] }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [mounted, setMounted] = useState(false);
  const total = slides.length;

  useEffect(() => {
    const id = window.setTimeout(() => setMounted(true), 1200);
    return () => window.clearTimeout(id);
  }, []);

  useEffect(() => {
    if (paused) return;
    const id = window.setInterval(() => setIndex((i) => (i + 1) % total), 6500);
    return () => window.clearInterval(id);
  }, [paused, total]);

  const go = (dir: number) => setIndex((i) => (i + dir + total) % total);

  return (
    <section
      className="relative overflow-hidden text-white"
      aria-roledescription="carousel"
      aria-label="Destaques Fisio em Ortopedia"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative">

        {slides.map((slide, i) => {
          const active = i === index;
          return (
            <BannerSlideView
              key={slide.key}
              slide={slide}
              index={i}
              total={total}
              ariaHidden={!active}
              showBgImage={i === 0 || mounted}
              className={`${active ? "relative opacity-100" : "absolute inset-0 opacity-0 pointer-events-none"} transition-opacity duration-700`}
            />
          );
        })}

      </div>


      {/* Controls */}
      <div className="absolute bottom-3 left-0 right-0 flex items-center justify-between px-4 pointer-events-none sm:inset-y-0 sm:bottom-0 sm:px-4 sm:gap-0">
        <button
          type="button"
          onClick={() => go(-1)}
          aria-label="Slide anterior"
          className="pointer-events-auto h-10 w-10 sm:h-11 sm:w-11 rounded-full bg-black/30 hover:bg-black/50 backdrop-blur-sm text-white flex items-center justify-center transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          type="button"
          onClick={() => go(1)}
          aria-label="Próximo slide"
          className="pointer-events-auto h-10 w-10 sm:h-11 sm:w-11 rounded-full bg-black/30 hover:bg-black/50 backdrop-blur-sm text-white flex items-center justify-center transition-colors"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Dots */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {slides.map((s, i) => (
          <button
            key={s.key}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`Ir para o slide ${i + 1}`}
            aria-current={i === index}
            className={`h-2 rounded-full transition-all ${i === index ? "w-8 bg-white" : "w-2 bg-white/40 hover:bg-white/70"}`}
          />
        ))}
      </div>
    </section>
  );
}


function HomePage() {
  const { banner } = Route.useLoaderData();
  const blackfeo = useBlackfeo();
  // O slide da BLACKFEO só entra quando a ação está liberada (ou ?preview=1);
  // durante a BLACKFEO, o slide da Black em Dobro sai.
  const base = selectSlidesFromConfig(banner).filter(
    (sl) => sl.key !== "blackfeo" || blackfeo,
  );
  const slides = blackfeo
    ? base.filter((sl) => sl.key !== "black2x1")
    : base;
  return (
    <div className="min-h-screen bg-white text-[#13123a]">
      <SiteHeader />

      {/* Rotating Banner */}
      <RotatingBanner slides={slides} />

      <h1 className="sr-only">
        Fisio em Ortopedia: pós-graduações e certificações em fisioterapia musculoesquelética
      </h1>






      {/* Features */}
      <section className="py-20 md:py-28 bg-white">

        <div className="container mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#13123a]">
              Movimente o seu futuro profissional
            </h2>
            <p className="mt-4 text-[var(--brand-soft)] font-semibold text-base sm:text-lg">
              Escolha o Portal Fisio em Ortopedia
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12 max-w-5xl mx-auto">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="text-center sm:text-left flex flex-col sm:flex-row gap-5 items-center sm:items-start">
                  <div className="w-14 h-14 rounded-2xl border border-[#13123a]/10 bg-white flex items-center justify-center shrink-0 shadow-sm">
                    <Icon className="w-7 h-7 text-[var(--brand-soft)]" strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-bold text-lg text-[#13123a] mb-1">{f.title}</h3>
                    <p className="text-[#13123a]/70 text-sm leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="py-20 md:py-28 bg-[#f7f7fc]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#13123a]">
              Cursos e Pós-Graduação
            </h2>
            <p className="mt-4 text-[#13123a]/70 text-base sm:text-lg">
              Aqui no Portal Fisio em Ortopedia você pode escolher a sua jornada de aprendizagem!
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {PRODUCTS.map((p) => (
              <article key={p.tag} className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow flex flex-col">
                <div className="aspect-[3/2] overflow-hidden">
                  <img src={p.img} alt={p.imgAlt} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="text-[var(--brand-soft)] font-extrabold uppercase tracking-widest text-base sm:text-lg">{p.tag}</h3>
                  <p className="mt-3 text-[#13123a]/80 text-sm leading-relaxed flex-1">{p.desc}</p>
                  {p.internal ? (
                    <Link
                      to={p.href}
                      className="mt-5 inline-flex items-center gap-2 text-[#13123a] font-bold text-sm hover:text-[var(--brand-soft)] transition-colors group"
                    >
                      {p.cta} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  ) : (
                    <a
                      href={p.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-5 inline-flex items-center gap-2 text-[#13123a] font-bold text-sm hover:text-[var(--brand-soft)] transition-colors group"
                    >
                      {p.cta} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>


      {/* About */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#13123a] leading-tight">
              Fisio em Ortopedia: <span className="text-[#5a62ff]">mais do que educação.</span>&nbsp;Um portal de oportunidades.
            </h2>
            <div className="mt-8 text-[#13123a]/75 space-y-5 text-base sm:text-lg leading-relaxed">
              <p>
                O Portal Fisio em Ortopedia, idealizado por quatro fisioterapeutas, nasceu com o propósito de democratizar a educação e promover conhecimento clínico e científico de qualidade sobre fisioterapia musculoesquelética para os profissionais da área, trazendo inovação, aprendizado contínuo e suprindo as necessidades de todas as pessoas que escolheram a fisioterapia como profissão.
              </p>
              <p>
                Hoje, mais do que um portal com foco em educação, somos um portal de oportunidades.
              </p>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {STATS.map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-5xl sm:text-6xl font-extrabold text-[#5a62ff]">{s.value}</div>
                <p className="mt-2 text-[#13123a]/70 text-sm sm:text-base font-medium">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founders */}
      <section className="py-20 md:py-28 bg-[#13123a] text-white">
        <div className="container mx-auto px-6">
          <h2 className="text-center text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-14">
            Idealizadores do <span className="text-[#95a2ff]">Portal</span>
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {FOUNDERS.map((f) => (
              <div key={f.name} className="text-center">
                <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-white/5">
                  <img src={f.photo} alt={f.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <h3 className="mt-4 font-bold text-base sm:text-lg uppercase tracking-wide">{f.name}</h3>
                <p className="text-white/60 text-xs sm:text-sm">{f.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Corpo Docente */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="rounded-3xl bg-[#f5f5fb] border border-[#13123a]/10 p-8 sm:p-12 grid lg:grid-cols-[1.2fr_1fr] gap-8 items-center">
            <div>
              <p className="text-[#3b42d1] font-semibold uppercase tracking-widest text-xs mb-3">
                Corpo docente
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#13123a] leading-tight">
                Mais de 80 professores referência em <span className="text-[#3b42d1]">fisioterapia musculoesquelética</span>
              </h2>
              <p className="mt-5 text-[#13123a]/75 text-base leading-relaxed">
                Especialistas, mestres e doutores que assinam as nossas pós-graduações
                em Orto, Dor, Esporte, Gerontologia, Traumato-ortopedia e Prescrição
                de Exercícios. Veja quem ensina em cada programa.
              </p>
              <div className="mt-6">
                <Link
                  to="/professores"
                  className="inline-flex items-center gap-2 rounded-full bg-[#13123a] text-white font-bold px-6 py-3 hover:bg-[#13123a]/90 transition-colors text-sm"
                >
                  Conheça o corpo docente
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 justify-items-center">
              {DOCENTE_PREVIEW.map((d) => (
                <img
                  key={d.nome}
                  src={d.foto}
                  alt={`${d.nome}, professor(a) das pós-graduações da Fisio em Ortopedia`}
                  loading="lazy"
                  className="h-14 w-14 sm:h-20 sm:w-20 rounded-full object-cover border-2 border-white shadow-sm"
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <BlackDestaqueSection origem="home" />

      <BlackPopupRolagem origem="home" />

      {/* Depoimentos */}
      <section className="py-16 md:py-20 bg-[#f7f7fc]">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-12">
            <p className="text-[#3b42d1] font-semibold uppercase tracking-widest text-xs mb-3">
              Depoimentos
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#13123a] leading-tight">
              O que diz quem já estudou na <span className="text-[#3b42d1]">FEO</span>
            </h2>
            <p className="mt-4 text-[#13123a]/75 text-base max-w-2xl mx-auto">
              Depoimentos reais de fisioterapeutas que fizeram pós-graduação e cursos na Fisio em Ortopedia.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {DEPOIMENTOS.slice(0, 3).map((d) => (
              <article
                key={d.id}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm flex flex-col"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center text-slate-600 font-semibold shrink-0">
                    {d.author_name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-sm text-[#13123a] truncate">{d.author_name}</h3>
                    <StarRating n={d.rating} />
                  </div>
                </div>
                <p className="text-[#13123a]/70 text-sm leading-relaxed flex-1 line-clamp-5">
                  “{d.comment}”
                </p>
              </article>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/depoimentos"
              className="inline-flex items-center gap-2 rounded-full bg-[#13123a] text-white font-bold px-6 py-3 hover:bg-[#13123a]/90 transition-colors text-sm"
            >
              Ver todos os depoimentos <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>


      {/* CTA */}
      <section className="py-20 md:py-24 bg-gradient-to-br from-[#f7f7fc] to-white">
        <div className="container mx-auto px-6 text-center max-w-3xl">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#13123a] leading-tight">
            Torne-se um fisioterapeuta <span className="text-[#5a62ff]"><br />do futuro!</span>
          </h2>
          <p className="mt-5 text-[#13123a]/75 text-base sm:text-lg leading-relaxed">
            Se você busca suporte para sua carreira, valorização profissional e otimização do seu tempo e rotina, o Portal Fisio em Ortopedia é o seu melhor movimento rumo ao futuro profissional.
          </p>
          <Link
            to="/pos"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[var(--brand-ink)] hover:brightness-110 text-[#13123a] font-bold px-8 py-4 text-sm sm:text-base transition-all"
          >
            Comece sua jornada <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
